import { isNullOrUndefined } from "../../../../../../../../tools";
import { IdProvider } from "../../../../../../../services/idprovider.service";
import { ServerCommunicatorService } from "../../../../../../../services/server-communicator.service";
import { UIUtils } from "../../../../../../../services/uiutils.service";
import { Utils } from "../../../../../../../services/utils.service";
import { DataContainer } from "../../../../../../infrastructure/datacontainer";
import { DataContainerService } from "../../../../../../infrastructure/datacontainer.service";
import { RequestTypes } from "../../../../../../infrastructure/enums";
import { IPersistable } from "../../../../../../infrastructure/IPersistable";
import { PayloadPacketFacade } from "../../../../../../infrastructure/payloadpacket/payloadpacketfacade";
import { TransportData } from "../../../../../../infrastructure/transportdata";
import { ValidationResultAccumulator } from "../../../../../../infrastructure/validationresultaccumulator";
import {
  AFPostMasterFetchRequest,
  AFPostNameFetchRequest,
} from "./afpostfetchrequest";

export class AFPostMasterProps {

 public created_date: string = "";
  public updated_date: string = "";
  public created_by: number = 0;
  public updated_by: number = 0;
  public deleted_by: number = 0;

  public id: number = 0;
  public state_id: number = 0;
  public readonly state_name_en: string = "";
  public readonly state_name_mr: string = "";
  public zone_id: number = 0;
  public readonly zone_name: string = "";
  public district_id: number = 0;
  public phc_id: number = 0;

  public af_post_number: number = 0;
  public readonly district_name_en: string = "";
  public readonly district_name_mr: string = "";
  public block_id: number = 0;
  public bcm_post_id: number = 0;
  public readonly block_name_en: string = "";
  public readonly block_name_mr: string = "";
  public name: string = "";
  public month: string = "";
  public sanctioned_no_of_asha_in_af_post: number = 0;
  public assigned_sc_ids: any[] = []; // SC Refs in array
  public af_post_id: number = 0;
  public under_supervision: number = 0;
  public af_employee_record_id: number = 0;

  public financial_year_id: number = 0;
  public bcm_post_name: string = '';
  public phc_name: string = '';

  public readonly is_newly_created: boolean = false;

  private constructor(is_newly_created: boolean) {
    this.is_newly_created = is_newly_created;
  }

  public static Blank() {
    return new AFPostMasterProps(true);
  }
}

export class AFPostMaster implements IPersistable<AFPostMaster> {
  public static readonly mastertablename: string = "af_post";

  private constructor(
    public readonly p: AFPostMasterProps,
    public readonly AllowEdit: boolean
  ) {}

  public async EnsurePrimaryKeysWithValidValues(): Promise<void> {
    if (this.p.id === undefined || this.p.id === 0) {
      const newids = await IdProvider.GetInstance().GetNextEntityId();
      // const newids = await IdProvider.GetInstance().GetAllocateSingleIds();
      this.p.id = newids[0];
      if (this.p.id <= 0)
        throw new Error("Cannot assign Id. Please try again");
    }
  }

  public GetEditableVersion(): AFPostMaster {
    let newAFPostMaster: AFPostMasterProps = Utils.GetInstance().DeepCopy(
      this.p
    );
    return AFPostMaster.CreateInstance(newAFPostMaster, true);
  }

  public static CreateNewInstance() {
    return new AFPostMaster(AFPostMasterProps.Blank(), true);
  }

  public static CreateInstance(data: any, allowEdit: boolean) {
    return new AFPostMaster(data as AFPostMasterProps, allowEdit);
  }

  public CheckSaveValidity(
    _td: TransportData,
    vra: ValidationResultAccumulator
  ): void {
    if (!this.AllowEdit)
      vra.add("", "This object is not editable and hence cannot be saved.");
    if (!this.p.name || this.p.name.trim() == "") {
      vra.add("name", "AFPostMaster Name cannot be blank. ");
    }
    if (this.p.block_id <= 0)
      vra.add("block_id", "Block Name cannot be Blank.");

    // Validate Month (string but numeric)
    const monthNum = Number(this.p.month);
    if (!this.p.month || isNaN(monthNum) || monthNum < 1 || monthNum > 12) {
      vra.add("month", "Please select a valid month.");
    }
    // ✔ Correct validation for compulsory + > 0

    if (
      !this.p.sanctioned_no_of_asha_in_af_post ||
      this.p.sanctioned_no_of_asha_in_af_post <= 0
    ) {
      vra.add(
        "sanctioned_no_of_asha_in_af_post",
        "Sanctioned No. of ASHA must be greater than 0."
      );
    }
  }

  public MergeIntoTransportData(td: TransportData) {
    DataContainerService.GetInstance().MergeIntoContainer(
      td.MainData,
      AFPostMaster.mastertablename,
      this.p
    );
  }

  private static m_currentInstance: AFPostMaster =
    AFPostMaster.CreateNewInstance();

  public static GetCurrentInstance() {
    return AFPostMaster.m_currentInstance;
  }

  public static SetCurrentInstance(value: AFPostMaster) {
    AFPostMaster.m_currentInstance = value;
  }

  // ********************************************
  public static cacheDataChangeLevel: number = -1;

  public static SingleInstanceFromTransportData(
    td: TransportData
  ): AFPostMaster {
    let dcs = DataContainerService.GetInstance();
    if (dcs.CollectionExists(td.MainData, AFPostMaster.mastertablename)) {
      for (let data of dcs.GetCollection(
        td.MainData,
        AFPostMaster.mastertablename
      )!.Entries) {
        return AFPostMaster.CreateInstance(data, false);
      }
    }

    return null as any;
  }

  public static ListFromDataContainer(
    cont: DataContainer,
    filterPredicate: (arg0: any) => boolean = null as any,
    sortPropertyName: string = "Name"
  ): AFPostMaster[] {
    let result: AFPostMaster[] = [];

    let dcs = DataContainerService.GetInstance();

    if (dcs.CollectionExists(cont, AFPostMaster.mastertablename)) {
      let coll = dcs.GetCollection(cont, AFPostMaster.mastertablename)!;
      let entries = coll.Entries;

      if (!isNullOrUndefined(filterPredicate))
        entries = entries.filter(filterPredicate);

      if (sortPropertyName.trim().length > 0) {
        entries = Utils.OrderByPropertyName(entries, sortPropertyName);
      }

      for (let data of entries) {
        result.push(AFPostMaster.CreateInstance(data, false));
      }
    }

    return result;
  }

  public static ListFromTransportData(td: TransportData): AFPostMaster[] {
    return AFPostMaster.ListFromDataContainer(td.MainData);
  }

  public static async FetchTransportData(
    req: AFPostMasterFetchRequest,
    errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance()
      .GlobalUIErrorHandler
  ) {
    let tdRequest = req.FormulateTransportData();
    let pktRequest =
      PayloadPacketFacade.GetInstance().CreateNewPayloadPacket2(tdRequest);

    let tr = await ServerCommunicatorService.GetInstance().sendHttpRequest(
      pktRequest
    );
    if (!tr.Successful) {
      if (!isNullOrUndefined(errorHandler)) await errorHandler(tr.Message);
      return null;
    }

    let tdResponse = JSON.parse(tr.Tag) as TransportData;
    return tdResponse;
  }


      public static async FetchTransportDataForReportFilter(req: AFPostNameFetchRequest, errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
      let tdRequest = req.FormulateTransportData();
      let pktRequest = PayloadPacketFacade.GetInstance().CreateNewPayloadPacket2(tdRequest);
  
      let tr = await ServerCommunicatorService.GetInstance().sendHttpRequest(pktRequest);
      if (!tr.Successful) {
        if (!isNullOrUndefined(errorHandler)) await errorHandler(tr.Message);
        return null;
      }
  
      let tdResponse = JSON.parse(tr.Tag) as TransportData;
      return tdResponse;
    }
  

  public static async FetchInstance(
    ref: number,
    errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance()
      .GlobalUIErrorHandler
  ) {
    let req = new AFPostMasterFetchRequest();
    let tdResponse = (await AFPostMaster.FetchTransportData(
      req,
      errorHandler
    )) as TransportData;
    return AFPostMaster.SingleInstanceFromTransportData(tdResponse);
  }

  public static async FetchList(
    req: AFPostMasterFetchRequest,
    errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance()
      .GlobalUIErrorHandler
  ) {
    let tdResponse = (await AFPostMaster.FetchTransportData(
      req,
      errorHandler
    )) as TransportData;
    return AFPostMaster.ListFromTransportData(tdResponse);
  }

  public static async FetchEntireList(
    errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance()
      .GlobalUIErrorHandler
  ) {
    let req = new AFPostMasterFetchRequest();
    let tdResponse = (await AFPostMaster.FetchTransportData(
      req,
      errorHandler
    )) as TransportData;
    return AFPostMaster.ListFromTransportData(tdResponse);
  }

  public static async FetchEntireListByZoneDistrictBlockPhcRef(
    zone_id: number,
    district_id: number,
    block_id: number,
     bcm_post_id: number,
    phc_id: number,
    errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance()
      .GlobalUIErrorHandler
  ) {
    let req = new AFPostMasterFetchRequest();
    if (zone_id) {
      req.zone_ids.push(zone_id);
    }
    if (district_id) {
      req.district_ids.push(district_id);
    }
    if (block_id) {
      req.block_ids.push(block_id);
    }
     if (bcm_post_id) {
      req.bcm_post_ids.push(bcm_post_id);
    }
    if (phc_id) {
      req.phc_ids.push(phc_id);
    }
    let tdResponse = (await AFPostMaster.FetchTransportData(
      req,
      errorHandler
    )) as TransportData;
    return AFPostMaster.ListFromTransportData(tdResponse);
  }

  public static async FetchEntireListByPHC(
    phc_id: number,
    errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance()
      .GlobalUIErrorHandler
  ) {
    let req = new AFPostMasterFetchRequest();
    if (phc_id) {
      req.phc_ids.push(phc_id);
    }
    let tdResponse = (await AFPostMaster.FetchTransportData(
      req,
      errorHandler
    )) as TransportData;
    return AFPostMaster.ListFromTransportData(tdResponse);
  }

  public static async FetchEntireListWithName(
    errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance()
      .GlobalUIErrorHandler
  ) {
    let req = new AFPostNameFetchRequest();
    let tdResponse = (await AFPostMaster.FetchTransportData(
      req,
      errorHandler
    )) as TransportData;
    return AFPostMaster.ListFromTransportData(tdResponse);
  }

  public static async FetchEntireListByPHCRef(
    phc_id: number,
    errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance()
      .GlobalUIErrorHandler
  ) {
    let req = new AFPostNameFetchRequest();
    if (phc_id) {
      req.phc_ids.push(phc_id);
    }
    let tdResponse = (await AFPostMaster.FetchTransportData(
      req,
      errorHandler
    )) as TransportData;
    return AFPostMaster.ListFromTransportData(tdResponse);
  }


  // // get all asha for report filter
  //   public static async FetchEntireListForReportFilter(errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
  //     let req = new AFPostNameFetchRequest();
  //     let tdResponse = await AFPostMaster.FetchTransportData(req, errorHandler) as TransportData;
  //     return AFPostMaster.ListFromTransportData(tdResponse);
  //   }
  

     // get all asha post for report filter service file 
      public static async FetchEntireListForReportFilter(errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
        let req = new AFPostNameFetchRequest();
        let tdResponse = await AFPostMaster.FetchTransportDataForReportFilter(req, errorHandler) as TransportData;
        return AFPostMaster.ListFromTransportData(tdResponse);
      }
    



   public static async FetchEntireListByPHCandBCMPostRef(phc_id: number, bcm_post_id: number,errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance().GlobalUIErrorHandler) {
    let req = new AFPostNameFetchRequest();
    if (phc_id) {
      req.phc_ids.push(phc_id);
    }
     if (bcm_post_id) {
      req.bcm_post_ids.push(bcm_post_id);
    }
    let tdResponse = (await AFPostMaster.FetchTransportData(
      req,
      errorHandler
    )) as TransportData;
    return AFPostMaster.ListFromTransportData(tdResponse);
  }




  public async DeleteInstance(
    successHandler: () => Promise<void> = null!,
    errorHandler: (err: string) => Promise<void> = UIUtils.GetInstance()
      .GlobalUIErrorHandler
  ) {
    let tdRequest = new TransportData();
    tdRequest.RequestType = RequestTypes.Deletion;

    this.MergeIntoTransportData(tdRequest);
    let pktRequest =
      PayloadPacketFacade.GetInstance().CreateNewPayloadPacket2(tdRequest);

    let tr = await ServerCommunicatorService.GetInstance().sendHttpRequest(
      pktRequest
    );
    if (!tr.Successful) {
      if (!isNullOrUndefined(errorHandler)) await errorHandler(tr.Message);
    } else {
      if (!isNullOrUndefined(successHandler)) await successHandler();
    }
  }
}
